import {
  useSubscriptionOffer,
  useHasCustomerAccount,
  useGetUserMemberships
} from "@/api";
import { TypographyBase } from "@/components/typography/Base";
import { TypographyH3 } from "@/components/typography/H3";
import { TypographyMuted } from "@/components/typography/Muted";
import { TypographySM } from "@/components/typography/SM";
import Alert from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import Loading from "@/components/ui/loading";
import { LoadingCircle } from "@/components/ui/loadingCircle";
import { AppCard } from "@/features/organization-apps";
import { SubscriptionOfferStatus } from "@/gql/__generated__/graphql";
import { Link, Navigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useHandleSubscriptionOfferAccept } from "../hooks/handleSubscriptionOfferAccept";
import { useHandleSubscriptionOfferReject } from "../hooks/handleSubscriptionOfferReject";
import { InvalidStatusAlert } from "./InvalidStatusAlert";
import NoCustomerAccount from "./NoCustomerAccount";
import OrganizationSelect from "./OrganizationSelect";

const ViewSubscriptionOffer = ({
  subscriptionOfferId
}: {
  subscriptionOfferId: string;
}) => {
  const [selectedOrganizationSlug, setSelectedOrganizationSlug] =
    useState<string>();

  const {
    data: subscriptionOffer,
    loading: loadingSubscriptionOffer,
    error: subscriptionOfferError
  } = useSubscriptionOffer(subscriptionOfferId);

  const { data: hasCustomerAccountData, loading: loadingHasCustomerAccount } =
    useHasCustomerAccount({
      organizationSlug: selectedOrganizationSlug || "",
      skip:
        subscriptionOffer?.__typename === "SubscriptionOffer" &&
        !subscriptionOffer.payment
    });

  const {
    data: membershipsUnfiltered,
    loading: loadingMemberships,
    error: membershipsError
  } = useGetUserMemberships();

  const memberships = useMemo(() => {
    const offeringOrganization =
      subscriptionOffer?.__typename === "SubscriptionOffer" &&
      subscriptionOffer.offeringOrganization.id;
    if (!offeringOrganization) return membershipsUnfiltered;

    return membershipsUnfiltered.filter(
      (m) => m.organization.id !== offeringOrganization
    );
  }, [membershipsUnfiltered, subscriptionOffer]);

  const { handleAccept, loadingResolveAccept } =
    useHandleSubscriptionOfferAccept({
      subscriptionOfferId
    });
  const { handleReject, loadingResolveReject } =
    useHandleSubscriptionOfferReject({
      subscriptionOfferId
    });

  const [disableSubmit, setDisableSubmit] = useState(memberships.length > 1);

  if (subscriptionOfferError || membershipsError)
    return (
      <div className="h-screen w-full flex justify-center items-center dark:bg-brand-gray-950">
        <Alert
          title="Unable to load subscription offer"
          description="An error occurred while loading the subscription offer. Please try to reload the page or report the issue."
          data-testid="errorAlert"
        />
      </div>
    );

  if (loadingSubscriptionOffer || loadingMemberships)
    return (
      <div className="h-screen w-full flex justify-center items-center dark:bg-brand-gray-950">
        <Loading size="md" />
      </div>
    );

  if (memberships.length < 1)
    return (
      <Navigate
        to="/organization-create"
        search={{
          redirect: {
            to: "/subscription-offer/$subscriptionOfferId",
            params: { subscriptionOfferId } as any
          }
        }}
      />
    );

  if (
    !subscriptionOffer ||
    subscriptionOffer.__typename === "SubscriptionOfferNotFound"
  )
    return (
      <div className="w-full flex justify-center pt-24">
        <Alert
          title="Unable to find subscription offer"
          description={
            <>
              We could not find the subscription offer. Either the offer has
              already been processed or an error occurred. <br /> <br /> Please
              check that the link you&apos;ve entered is correct or contact the
              organization that sent you the offer. If you think something went
              wrong, please report the issue.
            </>
          }
        />
      </div>
    );

  if (subscriptionOffer.status !== SubscriptionOfferStatus.Pending) {
    return (
      <div className="w-full flex justify-center pt-24">
        <InvalidStatusAlert status={subscriptionOffer.status} />
      </div>
    );
  }

  const displayNoCustomerAccount = Boolean(
    selectedOrganizationSlug &&
      subscriptionOffer.payment &&
      hasCustomerAccountData?.organization.__typename === "Organization" &&
      !hasCustomerAccountData?.organization.hasCustomerAccount
  );

  return (
    <form
      onSubmit={handleAccept}
      onChange={(e) => setDisableSubmit(!e.currentTarget.checkValidity())}
    >
      <TypographyH3>Review subscription offer</TypographyH3>
      <TypographySM className="dark:text-brand-gray-300">
        Details about your offer.
      </TypographySM>

      <div className="flex flex-col gap-4 mt-8">
        <TypographyBase className="py-1 border-b dark:border-brand-gray-600">
          App information
        </TypographyBase>
        <AppCard app={subscriptionOffer.app} disableLink />
        <TypographyBase className="py-1 border-b dark:border-brand-gray-600 mt-4">
          Offer details
        </TypographyBase>
        <div className="grid grid-cols-[30ch_1fr] gap-y-6">
          <Label>Organization</Label>
          <TypographySM className="text-end">
            {subscriptionOffer.offeringOrganization.name}
          </TypographySM>

          <Label>Price</Label>
          <TypographySM data-testid="price" className="text-end">
            {subscriptionOffer.payment
              ? `$${subscriptionOffer.payment.monthlyPriceUSD}`
              : "Free"}
          </TypographySM>

          {subscriptionOffer.payment && (
            <>
              <Label>Trial period</Label>
              <TypographySM data-testid="trialPeriod" className="text-end">
                {subscriptionOffer.payment.trialDays
                  ? `${subscriptionOffer.payment.trialDays} days`
                  : "None"}
              </TypographySM>
            </>
          )}
          {subscriptionOffer.message && (
            <>
              <Label>Message</Label>
              <TypographySM className="text-end">
                {subscriptionOffer.message}
              </TypographySM>
            </>
          )}
          {subscriptionOffer.expiresAt && (
            <>
              <Label>Expiration</Label>
              <TypographySM className="text-end">
                {format(subscriptionOffer.expiresAt, "PPP")}
              </TypographySM>
            </>
          )}
        </div>
        <TypographyBase className="py-1 border-b dark:border-brand-gray-600 mt-4">
          Subscription details
        </TypographyBase>
        <div className="grid grid-cols-[30ch_1fr] justify-items-end">
          <div>
            <Label className="pt-[11px]" htmlFor="organization">
              Organization
            </Label>
            <TypographyMuted className="dark:text-brand-gray-500">
              The offer will be accepted on behalf of the selected organization
            </TypographyMuted>
          </div>
          <div className="w-full max-w-80">
            <OrganizationSelect
              memberships={memberships}
              setSelectedOrganizationSlug={setSelectedOrganizationSlug}
            />
          </div>
          <Link
            to="/organization-create"
            search={{
              redirect: {
                to: "/subscription-offer/$subscriptionOfferId",
                params: { subscriptionOfferId: subscriptionOffer.id } as any
              }
            }}
            className="place-self-start mt-4"
          >
            <Button variant="outline">Create new organization</Button>
          </Link>
        </div>
        {displayNoCustomerAccount && selectedOrganizationSlug && (
          <NoCustomerAccount organizationSlug={selectedOrganizationSlug} />
        )}
        {!displayNoCustomerAccount && (
          <div className="flex justify-end items-center gap-3 mt-8">
            {(loadingHasCustomerAccount ||
              loadingResolveAccept ||
              loadingResolveReject) && <LoadingCircle type="default" />}
            <Button
              variant="outline"
              type="button"
              onClick={handleReject}
              disabled={loadingResolveAccept || loadingResolveReject}
              data-testid="rejectButton"
            >
              Reject
            </Button>

            <Button
              variant="default"
              type="submit"
              disabled={
                disableSubmit ||
                loadingResolveAccept ||
                loadingResolveReject ||
                loadingHasCustomerAccount ||
                !selectedOrganizationSlug
              }
              data-testid="acceptButton"
            >
              {subscriptionOffer.payment ? "Go to checkout" : "Accept offer"}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
};

export default ViewSubscriptionOffer;
